import { useCallback, useState } from 'react';
import {
  AnalyticsClient,
  useAnalyticsClient,
} from '../../../utilities/analytics/analytics-web-client';
import {
  WebAuthnAuthenticationErrorCode,
  challengeWithAuthenticator,
  isWebAuthnSupported,
} from '../../../helpers/webauthn';
import { PublicKeyCredentialWithAssertionJSON } from '@github/webauthn-json';
import { PublicKeyCredentialRequestOptionsJSON } from '@github/webauthn-json/dist/types/basic/json';
import { useNavigateSoftOrHard } from '../../../utilities/hooks/useNavigateSoftOrHard';
import { loginPageId } from '../constants';
import { useRegisterComponent } from '../../orchestrator';
import { useAppendReasonViewingScreen } from './utility';
import { useSearchParams } from 'react-router-dom';
import { LoginErrorCode } from '../../../types';

type PasskeyLoginOptions = {
  onSuccess?: () => void;
};

export const usePasskeyLogin = ({ onSuccess }: PasskeyLoginOptions = {}) => {
  useRegisterComponent(usePasskeyLogin.name);

  const [searchParams] = useSearchParams();
  const navigate = useNavigateSoftOrHard();
  const analyticsClient = useAnalyticsClient();
  const appendReasonViewingScreen = useAppendReasonViewingScreen();
  const [authenticating, setAuthenticating] = useState(false);

  const passkeyButtonClickedEvent = useCallback(() => {
    analyticsClient.buttonClickedEvent(
      loginPageId,
      `PasskeyLoginButton`,
      appendReasonViewingScreen({})
    );
  }, [analyticsClient, appendReasonViewingScreen]);

  const redirectWithCallback = useCallback(
    (url: URL) => {
      onSuccess?.();
      navigate(url);
    },
    [onSuccess, navigate]
  );

  const onErrorWithTrackingAnalytics = useCallback(
    (error: ChallengeError) => {
      analyticsClient.trackingEvent({
        page: loginPageId,
        action: 'failed',
        subject: ANALYTIC_EVENT_SUBJECT_ID,
        attributes: { reason: error },
      });
    },
    [analyticsClient]
  );

  const startPasskeyChallenge = useCallback(async () => {
    const isSupported = isWebAuthnSupported();

    analyticsClient.trackingEvent({
      page: loginPageId,
      action: 'started',
      subject: ANALYTIC_EVENT_SUBJECT_ID,
    });

    if (!isSupported) {
      const redirectUri = redirectToErrorPage({
        analyticsClient: analyticsClient,
        loginError: 'webauthn_not_supported',
        currentQueryParams: searchParams,
      });

      return redirectWithCallback(redirectUri);
    }

    try {
      setAuthenticating(true);

      const passkeyChallengeResponseResult = await challenge();

      if (!passkeyChallengeResponseResult.success) {
        const redirectUri = redirectToErrorPage({
          analyticsClient: analyticsClient,
          loginError: passkeyChallengeResponseResult.error,
          currentQueryParams: searchParams,
        });

        return redirectWithCallback(redirectUri);
      }

      const { requestOptions, transactionToken } = passkeyChallengeResponseResult.data;

      const authenticatorChallengeResult = await challengeWithAuthenticator(requestOptions);

      if (!authenticatorChallengeResult.success) {
        const redirectUri = redirectToErrorPage({
          analyticsClient: analyticsClient,
          loginError: 'webauthn_challenge_failed',
          currentQueryParams: searchParams,
          webAuthnError: authenticatorChallengeResult.error,
        });

        return redirectWithCallback(redirectUri);
      }

      const passkeyCredentialJson = authenticatorChallengeResult.data;

      const verifyResponseResult = await verify({
        transactionToken: transactionToken,
        assertionJson: passkeyCredentialJson,
        analyticsClient: analyticsClient,
      });

      if (!verifyResponseResult.success) {
        const redirectUri = redirectToErrorPage({
          analyticsClient: analyticsClient,
          loginError: verifyResponseResult.error,
          currentQueryParams: searchParams,
        });

        return redirectWithCallback(redirectUri);
      }

      return redirectWithCallback(verifyResponseResult.data.redirectUri);
    } catch (e) {
      return onErrorWithTrackingAnalytics(PasskeyChallengeApiError.challengeFailure);
    } finally {
      setAuthenticating(false);
    }
  }, [analyticsClient, onErrorWithTrackingAnalytics, redirectWithCallback, searchParams]);

  const handlePasskeyLogin = useCallback(() => {
    passkeyButtonClickedEvent();
    startPasskeyChallenge();
  }, [passkeyButtonClickedEvent, startPasskeyChallenge]);

  return {
    onPasskeyLogin: handlePasskeyLogin,
    authenticating,
  };
};

const ANALYTIC_EVENT_SUBJECT_ID = 'challengePasskey';

enum PasskeyChallengeApiError {
  challengeFailure = 'challengeFailure',
  tooManyRequests = 'tooManyRequests',
}

type ChallengeError = PasskeyChallengeApiError | WebAuthnAuthenticationErrorCode;
type Result<T, E> = { success: true; data: T } | { success: false; error: E };
type PasskeyChallengeResponseResult = Result<PasskeyChallengeResponse, LoginErrorCode>;

interface PasskeyChallengeResponse {
  transactionToken: string;
  requestOptions: PublicKeyCredentialRequestOptionsJSON;
}

const challenge = async (): Promise<PasskeyChallengeResponseResult> => {
  try {
    const response = await fetch(`/v2/passkey/challenge${location.search}`, { method: 'GET' });

    switch (response.status) {
      case 200:
        const challengeResponse = (await response.json()) as PasskeyChallengeResponse;
        return { success: true, data: challengeResponse };
      case 429:
        return { success: false, error: 'too_many_requests' };
      case 400:
        return { success: false, error: await errorFromBadRequest(response) };
      default:
        throw response;
    }
  } catch (error) {
    throw new Error(`error from fetch: /v2/passkey/challenge`);
  }
};

type VerifyProps = {
  transactionToken: string;
  assertionJson: PublicKeyCredentialWithAssertionJSON;
  analyticsClient: AnalyticsClient;
};

type VerifyResponse = {
  redirectUri: URL;
};

type PasskeyVerifyResponseResult = Result<VerifyResponse, LoginErrorCode>;

const verify = async ({
  transactionToken,
  assertionJson,
}: VerifyProps): Promise<PasskeyVerifyResponseResult> => {
  try {
    const clientExtensionResultsJsonString = JSON.stringify(assertionJson.clientExtensionResults);
    const query = location.search
      ? `${location.search}&transactionToken=${transactionToken}`
      : '?transactionToken=' + transactionToken;

    const response = await fetch(`/v2/passkey/verify${query}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...assertionJson,
        clientExtensionResults: clientExtensionResultsJsonString,
      }),
    });

    switch (response.status) {
      case 200:
        const verifyResponse = (await response.json()) as VerifyResponse;
        return { success: true, data: verifyResponse };
      case 400:
        return { success: false, error: await errorFromBadRequest(response) };
      case 429:
        return { success: false, error: 'too_many_requests' };
      default:
        throw response;
    }
  } catch (error) {
    throw new Error(`error during verification of passkey`);
  }
};

const errorFromBadRequest: (response: Response) => Promise<LoginErrorCode> = response =>
  response
    .json()
    .then(body => {
      switch (body.context.reason) {
        case 'bad_request':
          return 'unknown';
        case 'unknown':
          return 'unknown';
        case 'login.form.token.expired':
          return 'login.form.token.expired';
        case 'login.form.invalid_token':
          return 'unknown';
        case 'invalid_passkey':
          return 'invalid_passkey';
        case 'passkey_managed_account':
          return 'passkey_managed_account';
        default:
          return 'unknown';
      }
    })
    .catch(() => 'unknown');

function redirectToErrorPage({
  analyticsClient,
  loginError,
  currentQueryParams,
  webAuthnError,
}: {
  analyticsClient: AnalyticsClient;
  loginError: LoginErrorCode;
  currentQueryParams: URLSearchParams;
  webAuthnError?: WebAuthnAuthenticationErrorCode;
}): URL {
  if (webAuthnError) {
    analyticsClient.trackingEvent({
      page: loginPageId,
      action: 'failed',
      subject: ANALYTIC_EVENT_SUBJECT_ID,
      attributes: { reason: webAuthnError },
    });
  } else {
    analyticsClient.trackingEvent({
      page: loginPageId,
      subject: ANALYTIC_EVENT_SUBJECT_ID,
      action: 'failed',
      attributes: { reason: loginError },
    });
  }

  currentQueryParams.delete('errorCode');
  currentQueryParams.set('errorCode', loginError);

  return new URL(`/login?${currentQueryParams.toString()}`, window.location.origin);
}
